/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useRef, useState } from "react";
import {
  Box,
  Button,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useLearnosityPlayer } from "../hooks/useLearnosityPlayer";
import { useLearnositySubmissionExtractor } from "../hooks/useLearnositySubmissionExtractor";
import { AssessmentLayout } from "./AssessmentLayout";
import type { AssessmentViewProps } from "./StandardAssessmentView";

const NAV_BAR_HEIGHT = 72;

const NavBarContainer = styled(Box)(({ theme }) => ({
  label: "NavBarContainer",
  position: "fixed",
  bottom: 0,
  left: 0,
  right: 0,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: "16px",
  padding: "16px 0",
  backgroundColor: theme.palette.background.default,
  borderTop: `1px solid ${theme.palette.divider}`,
  zIndex: 10,
}));

const NavBarSpacer = styled(Box)({
  label: "NavBarSpacer",
  height: `${NAV_BAR_HEIGHT}px`,
});

const NavButton = styled(Button)({
  label: "NavButton",
  fontSize: "1.25em",
  padding: "0.4em 0.9em",
});

const StepIndicatorsContainer = styled(Box)({
  label: "StepIndicatorsContainer",
  display: "flex",
  gap: "6px",
  alignItems: "center",
});

const StepButton = styled(Button)({
  label: "StepButton",
  minWidth: "36px",
  width: "36px",
  height: "36px",
  borderRadius: "50%",
  padding: 0,
  fontSize: "0.875rem",
  fontWeight: 600,
});

interface NavState {
  disabled: boolean;
  label: string;
  onClick: () => void;
}

export const LearnAssessmentView = ({
  ladder,
  learnosityInitOptions,
  sessionId,
  reviewMode = false,
  onExit,
  onActivityStarted,
  onActivitySubmitted,
  onSubmissionComplete,
}: AssessmentViewProps) => {
  const onActivityStartedRef = useRef(onActivityStarted);
  onActivityStartedRef.current = onActivityStarted;
  const onExitRef = useRef(onExit);
  onExitRef.current = onExit;

  const [navState, setNavState] = useState<NavState>({
    disabled: true,
    label: "Next",
    onClick: () => {},
  });
  const [currentStep, setCurrentStep] = useState(0);
  const [furthestStep, setFurthestStep] = useState(0);
  const [totalSteps, setTotalSteps] = useState(0);
  const [ready, setReady] = useState(false);
  const [showTryAgain, setShowTryAgain] = useState(false);

  const isLastItemRef = useRef(false);

  const { submitting, handleSubmitted } = useLearnositySubmissionExtractor({
    ladderType: ladder.ladderType,
    onActivitySubmitted,
    onSubmissionComplete,
  });

  const divIdRef = useRef<string>("");
  const itemsApiRef = useRef<any>(null);

  // Component-level mirror of the closure-local currentPosition
  const currentPositionRef = useRef(0);
  // Guard against spurious `changed` events fired by resetResponse()
  const isResettingRef = useRef(false);

  const handleActivityStarted = useCallback((itemsApi: any) => {
    onActivityStartedRef.current(itemsApi);
  }, []);

  const goNextOrSubmit = useCallback(() => {
    const api = itemsApiRef.current;
    if (!api) return;
    if (isLastItemRef.current) {
      if (!reviewMode) {
        api.submit({ show_submit_confirmation: false, show_submit_ui: false });
        handleSubmitted(api, divIdRef.current);
      }
    } else {
      if (!reviewMode) api.save();
      api.items().next();
    }
  }, [handleSubmitted, reviewMode]);

  const goPrevious = useCallback(() => {
    const api = itemsApiRef.current;
    if (!api) return;
    if (!reviewMode) api.save();
    api.items().previous();
  }, [reviewMode]);

  const goToStep = useCallback(
    (stepIndex: number) => {
      if (stepIndex === currentPositionRef.current) return;
      const api = itemsApiRef.current;
      if (!api) return;
      if (!reviewMode) api.save();
      api.items().goto(stepIndex);
    },
    [reviewMode],
  );

  const handleReady = useCallback(
    (itemsApiInstance: any) => {
      itemsApiRef.current = itemsApiInstance;

      // Build an index map from item reference → position so we can
      // determine the actual position on every item:load, including resume.
      const items = itemsApiInstance.getItems();
      const itemRefs = Object.keys(items);
      const totalItems = itemRefs.length;
      const refToIndex: Record<string, number> = {};
      itemRefs.forEach((ref, i) => {
        refToIndex[ref] = i;
      });
      setTotalSteps(totalItems);

      // Determine initial furthest position from already-attempted items
      // (handles resume of incomplete sessions)
      let furthestPosition = 0;
      itemRefs.forEach((ref, i) => {
        const status = items[ref].attempt_status;
        if (status === "fully_attempted" || status === "partially_attempted") {
          furthestPosition = i;
        }
      });

      // --- item:load handler ---
      itemsApiInstance.on("item:load", () => {
        setShowTryAgain(false);
        // Determine actual position from the current item reference.
        // This correctly handles resume/review where the first loaded
        // item may not be at position 0.
        const currentRef = itemsApiInstance.getCurrentItem()?.reference;
        const detectedPosition =
          currentRef != null ? (refToIndex[currentRef] ?? 0) : 0;
        currentPositionRef.current = detectedPosition;

        furthestPosition = Math.max(
          furthestPosition,
          currentPositionRef.current,
        );
        isLastItemRef.current = currentPositionRef.current >= totalItems - 1;
        const atFrontier = currentPositionRef.current >= furthestPosition;

        setCurrentStep(currentPositionRef.current);
        setFurthestStep(furthestPosition);

        const currentItem = itemsApiInstance.getCurrentItem();

        // Reset the frontier item's responses on every visit so the student
        // always starts fresh at their furthest point. This ensures `changed`
        // fires when they answer, giving the correct Next/Check behavior on
        // reload and when navigating back-and-forward through the ladder.
        // Skip reset when in review mode.
        if (atFrontier && currentItem.questions.length && !reviewMode) {
          const questionInstances = itemsApiInstance.questions();
          isResettingRef.current = true;
          currentItem.questions.forEach((qData: any) => {
            const q = questionInstances[qData.response_id];
            if (q?.resetResponse) q.resetResponse();
          });
          isResettingRef.current = false;
        }

        if (!currentItem.questions.length) {
          // Informational slide — free navigation
          setNavState({
            disabled: false,
            label: isLastItemRef.current ? "Done" : "Next",
            onClick: goNextOrSubmit,
          });
        } else if (reviewMode) {
          // Completed ladder review — free navigation, exit on last item
          setNavState({
            disabled: false,
            label: isLastItemRef.current ? "Done" : "Next",
            onClick: isLastItemRef.current
              ? () => onExitRef.current()
              : goNextOrSubmit,
          });
        } else if (!atFrontier) {
          // Behind frontier — free navigation
          setNavState({
            disabled: false,
            label: "Next",
            onClick: goNextOrSubmit,
          });
        } else {
          // At frontier (just reset) — wait for interaction
          setNavState({
            disabled: true,
            label: isLastItemRef.current ? "Done" : "Next",
            onClick: () => {},
          });
        }
      });

      // --- Per-question handlers ---

      const questions: any[] = Object.values(itemsApiInstance.questions());
      questions.forEach((question) => {
        const validation = question.getQuestion().validation;
        const isUnscored =
          !validation ||
          !Object.prototype.hasOwnProperty.call(validation, "valid_response");

        if (isUnscored) {
          question.on("changed", () => {
            if (isResettingRef.current) return;
            if (reviewMode) return;
            setNavState({
              disabled: false,
              label: isLastItemRef.current ? "Done" : "Next",
              onClick: goNextOrSubmit,
            });
          });
        } else {
          // Scored: Check Answer / retry at frontier, simple Next behind frontier
          const checkAnswer = () => {
            setNavState((prev) => ({ ...prev, disabled: true }));
            if (question.isValid()) {
              question.validate({
                showCorrectAnswers: true,
                showDistractorRationale: true,
              });
            } else {
              question.validate({
                showCorrectAnswers: false,
                showDistractorRationale: true,
              });
            }
          };

          question.on("changed", () => {
            if (isResettingRef.current) return;
            if (reviewMode) return;
            setShowTryAgain(false);
            if (currentPositionRef.current < furthestPosition) {
              // Behind frontier — free navigation
              setNavState({
                disabled: false,
                label: "Next",
                onClick: goNextOrSubmit,
              });
            } else {
              const unscored = !question.getScore();
              if (unscored) {
                // Unscored question at frontier — allow advancing without validation
                setNavState({
                  disabled: false,
                  label: isLastItemRef.current ? "Done" : "Next",
                  onClick: goNextOrSubmit,
                });
              } else {
                // At frontier — must check answer
                setNavState({
                  disabled: false,
                  label: "Check it",
                  onClick: checkAnswer,
                });
              }
            }
          });

          question.on("validated", () => {
            if (reviewMode) return;
            if (currentPositionRef.current < furthestPosition) {
              // Behind frontier — just show Next
              setNavState({
                disabled: false,
                label: "Next",
                onClick: goNextOrSubmit,
              });
              return;
            }
            if (question.isValid()) {
              // Correct at frontier — lock the question and allow advancing
              question.disable();
              setNavState({
                disabled: false,
                label: isLastItemRef.current ? "Done" : "Next",
                onClick: goNextOrSubmit,
              });
            } else {
              // Incorrect at frontier — show "Try Again" hint
              setShowTryAgain(true);
            }
          });
        }
      });

      setReady(true);
    },
    [goNextOrSubmit, reviewMode],
  );

  const {
    divId,
    loading: activityLoading,
    itemsApi,
    error: hadError,
  } = useLearnosityPlayer({
    learnosityInitOptions,
    sessionId,
    events: {
      "test:start": handleActivityStarted,
      "test:resume": handleActivityStarted,
    },
    onReady: handleReady,
  });

  divIdRef.current = divId;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <AssessmentLayout
      ladder={ladder}
      divId={divId}
      loading={activityLoading}
      submitting={submitting}
      error={hadError}
      onExit={onExit}
      itemsApi={itemsApi}
    >
      {ready && (
        <>
          <NavBarSpacer />
          <NavBarContainer>
            {isMobile && showTryAgain ? (
              <Typography
                sx={{
                  color: "#dd002f",
                  fontWeight: 700,
                  fontSize: "1.25em",
                }}
              >
                Try Again
              </Typography>
            ) : (
              <>
                <NavButton
                  variant="outlined"
                  disabled={currentStep <= 0}
                  onClick={goPrevious}
                >
                  Previous
                </NavButton>
                <StepIndicatorsContainer>
                  {isMobile ? (
                    <StepButton variant="contained" color="primary" disabled>
                      {currentStep + 1}
                    </StepButton>
                  ) : (
                    Array.from({ length: totalSteps }, (_, i) => (
                      <StepButton
                        key={i}
                        variant={i === currentStep ? "contained" : "outlined"}
                        color="primary"
                        disabled={i > furthestStep}
                        onClick={() => goToStep(i)}
                      >
                        {i + 1}
                      </StepButton>
                    ))
                  )}
                </StepIndicatorsContainer>
                <Box sx={{ position: "relative", display: "inline-flex" }}>
                  <NavButton
                    variant="contained"
                    color="primary"
                    disabled={navState.disabled}
                    onClick={navState.onClick}
                  >
                    {navState.label}
                  </NavButton>
                  {showTryAgain && (
                    <Typography
                      sx={{
                        position: "absolute",
                        left: "calc(100% + 12px)",
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "#dd002f",
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                      }}
                    >
                      Try Again
                    </Typography>
                  )}
                </Box>
              </>
            )}
          </NavBarContainer>
        </>
      )}
    </AssessmentLayout>
  );
};
